import React, { useContext, useEffect, useRef } from 'react'
import { useLocation, Redirect, useHistory } from 'react-router-dom'
import queryString from 'query-string'
import { routes, BASE_URL } from 'app/routes'
import { useGet, useMutate } from 'restful-react'
import {
  EditInviteResponse,
  AcceptInviteInput,
  AcceptInviteResponse,
  ApiError,
} from 'EstraSuppliers'
import { endpoints } from 'app/api/endpoints'
import { HeaderContext } from 'app/Contexts/HeaderProvider'
import styled from 'styled-components'
import { Container } from 'app/components/ui/Container'
import { Title } from 'app/components/ui/Title'
import { Text } from 'app/components/ui/Text'
import { Base } from 'app/components/ui/Base'
import { Button } from 'app/components/ui/Button'
import { FormUserInvite } from './FormUserInvite'
import { LoadingSpinner } from 'app/components/ui/LoadingSpinner'
import { ProfileStepsNav } from 'app/components/shared/ProfileStepsNav'

const initialValues = {
  password: '',
  passwordConfirmation: '',
}

export type UserInviteFormState = typeof initialValues

export const UserInvite: React.FC = () => {
  const headerCtx = useContext(HeaderContext)

  const headerTitle = 'Area fornitori Marketing'
  const headerSubTitle =
    'Perché anche la creatività ha bisogno di una lunga preparazione “amministrativa” ;)'
  const signUpText =
    'Il Servizio Marketing e Comunicazione del gruppo Estra utilizza questa piattaforma con l’obiettivo di snellire le procedure di richiesta/gestione dei preventivi.' +
    '\nQui, completata la registrazione, riceverai le richieste di preventivo alle quali potrai rispondere direttamente online mantenendole nel tuo storico.' +
    '\nLa piattaforma dà la possibilità di gestire un processo di preventivazione trasparente, efficace e ben strutturato.'

  useEffect(() => {
    if (headerCtx) {
      headerCtx.updateTitles(headerTitle, headerSubTitle)
      headerCtx.updateSubtitleVariant('login_signup')
    }
  }, [])

  // Get method from child to submit form here on the parent
  const submitUserInviteForm = useRef<() => void>(null)

  const { push } = useHistory()

  const goToDashboard = () => {
    push(routes.dashboard.makeUrl())
  }

  const { search } = useLocation()

  const queryObject = queryString.parse(search)
  const invitationToken = queryObject.invitation_token
    ? `${queryObject.invitation_token}`
    : undefined

  if (!invitationToken) {
    return <Redirect to={routes.login.makeUrl()} />
  }

  // GET USER DATA FROM TOKEN
  const {
    data: invitedUser,
    loading: isLoadingUserData,
    error: tokenError,
  } = useGet<EditInviteResponse, ApiError>({
    path: endpoints.acceptInvitation,
    queryParams: {
      invitation_token: invitationToken,
    },
  })

  // ACCEPT INVITE MUTATION
  const {
    mutate: acceptInviteMutation,
    loading: isDoingSignup,
    error: signupError,
  } = useMutate<AcceptInviteResponse, ApiError, {}, AcceptInviteInput>({
    verb: 'PUT',
    path: endpoints.completeInvitation,
  })

  if (isLoadingUserData) {
    return <LoadingSpinner />
  }

  if (tokenError || !invitedUser) {
    return (
      <TokenErrorContainer>
        <Title size={3}> Questo Link non è più valido:</Title>
        <Text variant='s24' mt={1}>
          <a
            href='#'
            onClick={e => {
              e.preventDefault()
              window.location.href = `/${BASE_URL}`
            }}
          >
            {' '}
            Clicca qui{' '}
          </a>
          per effettuare l&apos;accesso
        </Text>
      </TokenErrorContainer>
    )
  }

  return (
    <SignupWrapper>
      <SignupHeaderContainer>
        <ProfileStepsNav currentStep={'signup'}></ProfileStepsNav>
        <TextWrapper>
          <Text variant='s24' mt={1}>
            {signUpText}
          </Text>
        </TextWrapper>
      </SignupHeaderContainer>
      <SignupBase
        paddingTop={2}
        bgImage='https://res.cloudinary.com/estra/image/upload/v1611839756/estra20dev/area_fornitori/signup/signup_1_bg.svg'
        bgImageMobile='https://res.cloudinary.com/estra/image/upload/v1611839756/estra20dev/area_fornitori/signup/signup_1_bg.svg'
      >
        <SignupContainer>
          <Grid>
            <SignupTextColumn>
              <Text variant='s20' weight='700' mb={2}>
                STEP1: SCELTA PASSWORD
              </Text>
              <Text variant='s20'>
                Per accedere all’area sono necessari indirizzo e-mail e
                password. La prima è già inserita dal sistema, mentre la seconda
                la devi scegliere tu.{'\n'}Rispetta le indicazioni:
              </Text>
              <Text variant='s14' isItalic>
                Almeno 8 caratteri{'\n'}
                Almeno una lettera maiuscola (es. ABCD){'\n'}
                Almeno un numero (es. 1234){'\n'}
                Almeno un carattere speciale (es. !%$&amp;)
              </Text>
            </SignupTextColumn>
            <SignupFormColumn>
              <FormUserInvite
                ref={submitUserInviteForm}
                initialValues={{
                  email: invitedUser?.email,
                  password: '',
                  passwordConfirmation: '',
                }}
                onSignupRequest={({ password, passwordConfirmation }) => {
                  acceptInviteMutation({
                    api_v1_user: {
                      invitation_token: invitationToken as string,
                      password: password,
                      password_confirmation: passwordConfirmation,
                    },
                  }).then(res => {
                    if (res.email) {
                      goToDashboard()
                    }
                  })
                }}
                error={signupError}
              />
            </SignupFormColumn>
          </Grid>
          <ButtonWrapper>
            <Button
              size='lg'
              type='submit'
              disabled={isDoingSignup}
              onClick={() => {
                if (submitUserInviteForm.current) {
                  submitUserInviteForm.current()
                }
              }}
            >
              PROCEDI ALLO STEP 2
            </Button>
          </ButtonWrapper>
        </SignupContainer>
      </SignupBase>
    </SignupWrapper>
  )
}

const TokenErrorContainer = styled(Container)`
  text-align: center;
`

const SignupWrapper = styled.div``

const SignupHeaderContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;

  margin-bottom: 6rem;
`

const SignupBase = styled(Base)`
  margin-bottom: 0;

  @media (max-width: ${({ theme }) => theme.bp.desktop}) {
    background-position: bottom;
    padding-bottom: 3rem;
  }
`

const TextWrapper = styled.div`
  text-align: center;

  max-width: 900px;
  margin-right: 0;
  margin-left: 0;
`

const SignupContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Grid = styled.div`
  margin-bottom: 1rem;
  padding-top: 3rem;

  @media (min-width: ${({ theme }) => theme.bp.desktop}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;

    max-width: 1000px;

    margin-left: auto;
    margin-right: auto;
  }
`

const SignupTextColumn = styled(Text)`
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 3rem;

  @media (max-width: ${({ theme }) => theme.bp.desktop}) {
    text-align: center;
  }
`

const SignupFormColumn = styled.div`
  padding-top: 2rem;

  @media (max-width: ${({ theme }) => theme.bp.desktop}) {
    max-width: 340px;
    margin-left: auto;
    margin-right: auto;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`
